import { lazy } from "react";
import { Navigate } from "react-router-dom";
// auth
const Login = lazy(() => import("../app/(other)/auth/login/page"));
const Logout = lazy(() => import("../app/(other)/auth/logout/page"));
const ForgotPassword = lazy(() => import("../app/(other)/auth/forgot-password/page"));
const ResetPassword = lazy(() => import("../app/(other)/auth/reset-password/page"));

const AdminPage = lazy(() => import("../app/(admin)/admin/page"));
const ProfilePage = lazy(() => import("../app/(admin)/profile/page"));
const UsersPage = lazy(() => import("../app/(admin)/users/page"));
const AdminNotificationsPage = lazy(() => import("../app/(admin)/notifications/page"));

// dashboard
const Dashboard = lazy(() => import("../app/(admin)/dashboard/page"));
const Analytics = lazy(() => import("../app/(admin)/analytics/page"));
const BulkTools = lazy(() => import("../app/(admin)/bulk-tools/page"));

// new pages
const Product = lazy(() => import("../app/(admin)/product/page"));
const ProductEdit = lazy(() => import("../app/(admin)/product/edit/page"));
const Brand = lazy(() => import("../app/(admin)/brand/page"));
const Category = lazy(() => import("../app/(admin)/category/page"));
const Banner = lazy(() => import("../app/(admin)/banner/page"));
const Coupon = lazy(() => import("../app/(admin)/coupon/page"));
const CustomGoal = lazy(() => import("../app/(admin)/custom-goal/page"));
const SubcategoryPage = lazy(() =>
    import("../app/(admin)/category/SubcategoryPage")
);
const GoalCategoryPage = lazy(() =>
    import("../app/(admin)/goal-category/page")
);
const Boarding = lazy(() => import("../app/(admin)/boarding-system/page"));

// blog
const BlogCategoryPage = lazy(() => import("../app/(admin)/blog-category/page"));
const BlogSubCategoryPage = lazy(() => import("../app/(admin)/blog-subcategory/page"));
const BlogPostsPage = lazy(() => import("../app/(admin)/blog-posts/page"));
const BlogPostAdd = lazy(() => import("../app/(admin)/blog-posts/add/page"));
const BlogPostEdit = lazy(() => import("../app/(admin)/blog-posts/edit/page"));
const BlogPostPreview = lazy(() => import("../app/(admin)/blog-posts/preview/page"));
const BlogCommentsPage = lazy(() => import("../app/(admin)/blog-comments/page"));

const SpendTracker = lazy(() => import("../app/(admin)/spend-tracker/page"));

//apps
const Calendar = lazy(() => import("../app/(admin)/calendar/page"));

// pages
const StarterPage = lazy(() =>
    import("../app/(admin)/pages/starter-page/page")
);
const Pricing = lazy(() => import("../app/(admin)/pages/pricing/page"));
const FaqPages = lazy(() => import("../app/(admin)/pages/faqs/page"));
const FeatureVideo = lazy(() =>
    import("../app/(admin)/pages/videoInsight/page")
);
const MaintenancePages = lazy(() => import("../app/(other)/maintenance/page"));
const TimelinePages = lazy(() => import("../app/(admin)/pages/timeline/page"));
const ComingSoonPage = lazy(() => import("../app/(other)/coming-soon/page"));
const TermsConditions = lazy(() => import("../app/(admin)/pages/terms/page"));
const Error404Alt = lazy(() =>
    import("../app/(admin)/pages/error-404-alt/page")
);

// base ui
const Accordions = lazy(() => import("../app/(admin)/ui/accordions/page"));
const Alerts = lazy(() => import("../app/(admin)/ui/alerts/page"));
const Avatars = lazy(() => import("../app/(admin)/ui/avatars/page"));
const Badges = lazy(() => import("../app/(admin)/ui/badges/page"));
const Breadcrumb = lazy(() => import("../app/(admin)/ui/breadcrumb/page"));
const Buttons = lazy(() => import("../app/(admin)/ui/buttons/page"));
const Cards = lazy(() => import("../app/(admin)/ui/cards/page"));
const Carousel = lazy(() => import("../app/(admin)/ui/carousel/page"));
const Collapse = lazy(() => import("../app/(admin)/ui/collapse/page"));
const Dropdowns = lazy(() => import("../app/(admin)/ui/dropdowns/page"));
const Ratio = lazy(() => import("../app/(admin)/ui/ratio/page"));
const Grid = lazy(() => import("../app/(admin)/ui/grid/page"));
const Links = lazy(() => import("../app/(admin)/ui/links/page"));
const ListGroup = lazy(() => import("../app/(admin)/ui/list-group/page"));
const Modals = lazy(() => import("../app/(admin)/ui/modals/page"));
const Notifications = lazy(() =>
    import("../app/(admin)/ui/notifications/page")
);
const Offcanvas = lazy(() => import("../app/(admin)/ui/offcanvas/page"));
const Placeholders = lazy(() => import("../app/(admin)/ui/placeholders/page"));
const Pagination = lazy(() => import("../app/(admin)/ui/pagination/page"));
const Popovers = lazy(() => import("../app/(admin)/ui/popovers/page"));
const Progress = lazy(() => import("../app/(admin)/ui/progress/page"));
const Spinners = lazy(() => import("../app/(admin)/ui/spinners/page"));
const Tabs = lazy(() => import("../app/(admin)/ui/tabs/page"));
const Tooltips = lazy(() => import("../app/(admin)/ui/tooltips/page"));
const Typography = lazy(() => import("../app/(admin)/ui/typography/page"));
const Utilities = lazy(() => import("../app/(admin)/ui/utilities/page"));

// extended ui
const Dragula = lazy(() => import("../app/(admin)/extended/dragula/page"));
const SweetAlert = lazy(() =>
    import("../app/(admin)/extended/sweet-alert/page")
);
const Ratings = lazy(() => import("../app/(admin)/extended/ratings/page"));
const Scrollbar = lazy(() => import("../app/(admin)/extended/scrollbar/page"));

// icons
const Remix = lazy(() => import("../app/(admin)/icons/remix/page"));
const Lucide = lazy(() => import("../app/(admin)/icons/lucide/page"));
const Solar = lazy(() => import("../app/(admin)/icons/solar/page"));

// charts
const AreaCharts = lazy(() => import("../app/(admin)/charts/area/page"));
const BarCharts = lazy(() => import("../app/(admin)/charts/bar/page"));
const BubbleCharts = lazy(() => import("../app/(admin)/charts/bubble/page"));
const CandlestickCharts = lazy(() =>
    import("../app/(admin)/charts/candlestick/page")
);
const ColumnCharts = lazy(() => import("../app/(admin)/charts/column/page"));
const HeatmapCharts = lazy(() => import("../app/(admin)/charts/heatmap/page"));
const LineCharts = lazy(() => import("../app/(admin)/charts/line/page"));
const MixedCharts = lazy(() => import("../app/(admin)/charts/mixed/page"));
const TimelineCharts = lazy(() =>
    import("../app/(admin)/charts/timeline/page")
);
const BoxplotCharts = lazy(() => import("../app/(admin)/charts/boxplot/page"));
const TreemapCharts = lazy(() => import("../app/(admin)/charts/treemap/page"));
const PieCharts = lazy(() => import("../app/(admin)/charts/pie/page"));
const RadarCharts = lazy(() => import("../app/(admin)/charts/radar/page"));
const RadialBarCharts = lazy(() =>
    import("../app/(admin)/charts/radialBar/page")
);
const ScatterCharts = lazy(() => import("../app/(admin)/charts/scatter/page"));
const PolarCharts = lazy(() => import("../app/(admin)/charts/polar/page"));
const SparklinesCharts = lazy(() =>
    import("../app/(admin)/charts/sparklines/page")
);
const SlopeCharts = lazy(() => import("../app/(admin)/charts/slope/page"));
const FunnelCharts = lazy(() => import("../app/(admin)/charts/funnel/page"));

// tables
const BasicTables = lazy(() =>
    import("../app/(admin)/tables/basic-table/page")
);
const GridJsTables = lazy(() => import("../app/(admin)/tables/gridJs/page"));

// maps
const GoogleMaps = lazy(() => import("../app/(admin)/maps/google/page"));
const VectorMaps = lazy(() => import("../app/(admin)/maps/vector/page"));
const LeafletMaps = lazy(() => import("../app/(admin)/maps/leaflet/page"));

//invoices

const Invoices = lazy(() => import("../app/(admin)/invoices/page"));
const ViewInvoices = lazy(() =>
    import("../app/(admin)/invoices/view-invoice/page")
);
const CreateInvoices = lazy(() =>
    import("../app/(admin)/invoices/create-invoices/page")
);

// forms
const BasicElements = lazy(() => import("../app/(admin)/forms/basic/page"));
const Inputmask = lazy(() => import("../app/(admin)/forms/inputmask/page"));
const Picker = lazy(() => import("../app/(admin)/forms/picker/page"));
const Select = lazy(() => import("../app/(admin)/forms/select/page"));
const Slider = lazy(() => import("../app/(admin)/forms/slider/page"));
const Validation = lazy(() => import("../app/(admin)/forms/validation/page"));
const Wizard = lazy(() => import("../app/(admin)/forms/wizard/page"));
const FileUploads = lazy(() =>
    import("../app/(admin)/forms/file-uploads/page")
);
const Editors = lazy(() => import("../app/(admin)/forms/editors/page"));
const LayoutsForms = lazy(() => import("../app/(admin)/forms/layout/page"));

//layout
const Horizontal = lazy(() => import("../app/(other)/layouts/horizontal/page"));
const FullView = lazy(() => import("../app/(other)/layouts/full-view/page"));
const FullscreenView = lazy(() =>
    import("../app/(other)/layouts/fullscreen-view/page")
);
const HoverMenu = lazy(() => import("../app/(other)/layouts/hover-menu/page"));
const IconView = lazy(() => import("../app/(other)/layouts/icon-view/page"));
const Compact = lazy(() => import("../app/(other)/layouts/compact/page"));
const DarkMode = lazy(() => import("../app/(other)/layouts/dark-mode/page"));

// error
const Error401 = lazy(() => import("../app/(other)/errors/error-401/page"));
const Error400 = lazy(() => import("../app/(other)/errors/error-400/page"));
const Error403 = lazy(() => import("../app/(other)/errors/error-403/page"));
const Error404 = lazy(() => import("../app/(other)/errors/error-404/page"));
const Error500 = lazy(() => import("../app/(other)/errors/error-500/page"));
const ServiceUnavailable = lazy(() =>
    import("../app/(other)/errors/service-unavailable/page")
);
const initialRoutes = [
    {
        path: "/",
        name: "root",
        element: <Navigate to="/dashboard" />,
    },
];

// dashboards
const generalRoutes = [
    {
        path: "/dashboard",
        name: "Dashboard",
        element: <Dashboard />,
    },
    {
        path: "/admin",
        name: "Admin",
        element: <AdminPage />,
    },
    {
        path: "/profile",
        name: "Profile",
        element: <ProfilePage />,
    },
    {
        path: "/users",
        name: "Users",
        element: <UsersPage />,
    },
    {
        path: "/notifications",
        name: "Notifications",
        element: <AdminNotificationsPage />,
    },
    {
        path: "/analytics",
        name: "Analytics",
        element: <Analytics />,
    },
    {
        path: "/bulk-tools",
        name: "Bulk Tools",
        element: <BulkTools />,
    },
    {
        path: "/product",
        name: "Product",
        element: <Product />,
    },
    {
        path: "/product/edit/:id",
        name: "ProductEdit",
        element: <ProductEdit />,
    },
    {
        path: "/brand",
        name: "Brand",
        element: <Brand />,
    },
    {
        path: "/category",
        name: "Category",
        element: <Category />,
    },
    {
        path: "/goal-category",
        name: "Goal Category",
        element: <GoalCategoryPage />,
    },
    {
        path: "/boarding-system",
        name: "Boarding",
        element: <Boarding />,
    },
    {
        path: "/banner",
        name: "Banner",
        element: <Banner />,
    },
    {
        path: "/custom-goal",
        name: "Custom-Goal",
        element: <CustomGoal />,
    },
    {
        path: "/product-coupon",
        name: "Coupon",
        element: <Coupon />,
    },
    {
        path: "pages/feature-video",
        name: "Coupon",
        element: <FeatureVideo />,
    },
    {
        path: "/category/:id",
        name: "Subcategory",
        element: <SubcategoryPage />,
    },
    {
        path: "/spend-tracker",
        name: "Spend Tracker",
        element: <SpendTracker />,
    },
    {
        path: "/blog-category",
        name: "Blog Category",
        element: <BlogCategoryPage />,
    },
    {
        path: "/blog-subcategory",
        name: "Blog Sub-category",
        element: <BlogSubCategoryPage />,
    },
    {
        path: "/blog-posts",
        name: "Blog Posts",
        element: <BlogPostsPage />,
    },
    {
        path: "/blog-posts/add",
        name: "Add Blog Post",
        element: <BlogPostAdd />,
    },
    {
        path: "/blog-posts/edit/:id",
        name: "Edit Blog Post",
        element: <BlogPostEdit />,
    },
    {
        path: "/blog-posts/preview/:id",
        name: "Preview Blog Post",
        element: <BlogPostPreview />,
    },
    {
        path: "/blog-comments",
        name: "Blog Comments",
        element: <BlogCommentsPage />,
    },
];
const appsRoutes = [
    {
        path: "/calendar",
        name: "Calendar",
        element: <Calendar />,
    },
];

// pages
const customPagesRoutes = [
    {
        path: "/pages/starter-page",
        name: "Starter Page",
        element: <StarterPage />,
    },
    {
        path: "/pages/pricing",
        name: "Pricing",
        element: <Pricing />,
    },
    {
        path: "/pages/faqs",
        name: "FAQ",
        element: <FaqPages />,
    },
    {
        path: "/pages/terms",
        name: "Terms & Conditions",
        element: <TermsConditions />,
    },
    {
        path: "/pages/timeline",
        name: "Timeline",
        element: <TimelinePages />,
    },
    {
        path: "/pages/error-404-alt",
        name: "Error 404 Alt",
        element: <Error404Alt />,
    },
];
const layoutsRoutes = [
    {
        name: "Horizontal",
        path: "/horizontal",
        element: <Horizontal />,
    },
    {
        name: "Full view",
        path: "/full-view",
        element: <FullView />,
    },
    {
        name: "Fullscreen View",
        path: "/fullscreen-view",
        element: <FullscreenView />,
    },
    {
        name: "Hover Menu",
        path: "/hover-menu",
        element: <HoverMenu />,
    },
    {
        name: "Compact",
        path: "/compact",
        element: <Compact />,
    },
    {
        name: "Icon View",
        path: "/icon-view",
        element: <IconView />,
    },
    {
        name: "dark mode",
        path: "/dark-mode",
        element: <DarkMode />,
    },
];

// ui
const uiRoutes = [
    {
        path: "/ui/accordions",
        name: "Accordions",
        element: <Accordions />,
    },
    {
        path: "/ui/alerts",
        name: "Alerts",
        element: <Alerts />,
    },
    {
        path: "/ui/avatars",
        name: "Avatars",
        element: <Avatars />,
    },
    {
        path: "/ui/badges",
        name: "Badges",
        element: <Badges />,
    },
    {
        path: "/ui/breadcrumb",
        name: "Breadcrumb",
        element: <Breadcrumb />,
    },
    {
        path: "/ui/buttons",
        name: "Buttons",
        element: <Buttons />,
    },
    {
        path: "/ui/cards",
        name: "Cards",
        element: <Cards />,
    },
    {
        path: "/ui/carousel",
        name: "Carousel",
        element: <Carousel />,
    },
    {
        path: "/ui/collapse",
        name: "Collapse",
        element: <Collapse />,
    },
    {
        path: "/ui/dropdowns",
        name: "Dropdowns",
        element: <Dropdowns />,
    },
    {
        path: "/ui/ratio",
        name: "Ratio",
        element: <Ratio />,
    },
    {
        path: "/ui/grid",
        name: "Grid",
        element: <Grid />,
    },
    {
        path: "/ui/links",
        name: "Links",
        element: <Links />,
    },
    {
        path: "/ui/list-group",
        name: "List Group",
        element: <ListGroup />,
    },
    {
        path: "/ui/modals",
        name: "Modals",
        element: <Modals />,
    },
    {
        path: "/ui/notifications",
        name: "Notifications",
        element: <Notifications />,
    },
    {
        path: "/ui/offcanvas",
        name: "Offcanvas",
        element: <Offcanvas />,
    },
    {
        path: "/ui/placeholders",
        name: "Placeholders",
        element: <Placeholders />,
    },
    {
        path: "/ui/pagination",
        name: "Pagination",
        element: <Pagination />,
    },
    {
        path: "/ui/popovers",
        name: "Popovers",
        element: <Popovers />,
    },
    {
        path: "/ui/progress",
        name: "Progress",
        element: <Progress />,
    },
    {
        path: "/ui/spinners",
        name: "Spinners",
        element: <Spinners />,
    },
    {
        path: "/ui/tabs",
        name: "Tabs",
        element: <Tabs />,
    },
    {
        path: "/ui/tooltips",
        name: "Tooltips",
        element: <Tooltips />,
    },
    {
        path: "/ui/typography",
        name: "Typography",
        element: <Typography />,
    },
    {
        path: "/ui/utilities",
        name: "Utilities",
        element: <Utilities />,
    },
];
const iconsRoutes = [
    {
        path: "/icons/remix",
        name: "Remix",
        element: <Remix />,
    },
    {
        path: "/icons/lucide",
        name: "Lucide",
        element: <Lucide />,
    },
    {
        path: "/icons/solar",
        name: "Solar",
        element: <Solar />,
    },
];
const extendedRoutes = [
    {
        path: "/extended/dragula",
        name: "Dragula",
        element: <Dragula />,
    },
    {
        path: "/extended/sweet-alert",
        name: "Sweet Alert",
        element: <SweetAlert />,
    },
    {
        path: "/extended/ratings",
        name: "Ratings",
        element: <Ratings />,
    },
    {
        path: "/extended/scrollbar",
        name: "Scrollbar",
        element: <Scrollbar />,
    },
];
const chartsRoutes = [
    {
        path: "/charts/area",
        name: "Area Charts",
        element: <AreaCharts />,
    },
    {
        path: "/charts/bar",
        name: "Bar",
        element: <BarCharts />,
    },
    {
        path: "/charts/bubble",
        name: "Bubble",
        element: <BubbleCharts />,
    },
    {
        path: "/charts/candlestick",
        name: "Candlestick",
        element: <CandlestickCharts />,
    },
    {
        path: "/charts/column",
        name: "Column",
        element: <ColumnCharts />,
    },
    {
        path: "/charts/heatmap",
        name: "Heatmap",
        element: <HeatmapCharts />,
    },
    {
        path: "/charts/line",
        name: "Line",
        element: <LineCharts />,
    },
    {
        path: "/charts/mixed",
        name: "Mixed",
        element: <MixedCharts />,
    },
    {
        path: "/charts/timeline",
        name: "Timeline",
        element: <TimelineCharts />,
    },
    {
        path: "/charts/boxplot",
        name: "Boxplot",
        element: <BoxplotCharts />,
    },
    {
        path: "/charts/treemap",
        name: "Treemap",
        element: <TreemapCharts />,
    },
    {
        path: "/charts/pie",
        name: "Pie",
        element: <PieCharts />,
    },
    {
        path: "/charts/radar",
        name: "Radar",
        element: <RadarCharts />,
    },
    {
        path: "/charts/radialBar",
        name: "RadialBar",
        element: <RadialBarCharts />,
    },
    {
        path: "/charts/scatter",
        name: "Scatter",
        element: <ScatterCharts />,
    },
    {
        path: "/charts/polar",
        name: "Polar Area",
        element: <PolarCharts />,
    },
    {
        path: "/charts/sparklines",
        name: "Sparklines",
        element: <SparklinesCharts />,
    },
    {
        path: "/charts/slope",
        name: "Slope",
        element: <SlopeCharts />,
    },
    {
        path: "/charts/funnel",
        name: "Funnel",
        element: <FunnelCharts />,
    },
];
const formsRoutes = [
    {
        path: "/forms/basic",
        name: "Basic Elements",
        element: <BasicElements />,
    },
    {
        path: "/forms/inputmask",
        name: "Inputmask",
        element: <Inputmask />,
    },
    {
        path: "/forms/picker",
        name: "Picker",
        element: <Picker />,
    },
    {
        path: "/forms/select",
        name: "Select",
        element: <Select />,
    },
    {
        path: "/forms/slider",
        name: "Range Slider",
        element: <Slider />,
    },
    {
        path: "/forms/validation",
        name: "Validation",
        element: <Validation />,
    },
    {
        path: "/forms/wizard",
        name: "Wizard",
        element: <Wizard />,
    },
    {
        path: "/forms/file-uploads",
        name: "File Uploads",
        element: <FileUploads />,
    },
    {
        path: "/forms/editors",
        name: "Editors",
        element: <Editors />,
    },
    {
        path: "/forms/layout",
        name: "Layouts",
        element: <LayoutsForms />,
    },
];
const tablesRoutes = [
    {
        path: "/tables/basic-table",
        name: "Basic Tables",
        element: <BasicTables />,
    },
    {
        path: "/tables/gridJs",
        name: "GridJs Tables",
        element: <GridJsTables />,
    },
];
const mapsRoutes = [
    {
        path: "/maps/google",
        name: "Google Maps",
        element: <GoogleMaps />,
    },
    {
        path: "/maps/vector",
        name: "Vector Maps",
        element: <VectorMaps />,
    },
    {
        path: "/maps/leaflet",
        name: "Leaflet Maps",
        element: <LeafletMaps />,
    },
];
const invoicesRoutes = [
    {
        path: "/invoices",
        name: "Invoice",
        element: <Invoices />,
    },
    {
        path: "/invoices/view-invoice",
        name: "View Invoice",
        element: <ViewInvoices />,
    },
    {
        path: "/invoices/create-invoices",
        name: "Create Invoice",
        element: <CreateInvoices />,
    },
];

// auth
const authRoutes = [
    {
        path: "/auth/login",
        name: "Login",
        element: <Login />,
    },

    {
        path: "/auth/logout",
        name: "Logout",
        element: <Logout />,
    },

    {
        path: "/auth/forgot-password",
        name: "Forgot Password",
        element: <ForgotPassword />,
    },

    {
        path: "/auth/reset-password",
        name: "Reset Password",
        element: <ResetPassword />,
    },
];

// public routes
const otherPublicRoutes = [
    {
        path: "/errors/error-401",
        name: "401 Unauthorized",
        element: <Error401 />,
    },
    {
        path: "/errors/error-400",
        name: "400 Bad Reques",
        element: <Error400 />,
    },
    {
        path: "/coming-soon",
        name: "Coming Soon",
        element: <ComingSoonPage />,
    },
    {
        path: "/errors/error-403",
        name: "403 Forbidden",
        element: <Error403 />,
    },
    {
        path: "/errors/error-404",
        name: "404 Not Found",
        element: <Error404 />,
    },
    {
        path: "/errors/error-500",
        name: "500 Internal Server",
        element: <Error500 />,
    },
    {
        path: "/errors/service-unavailable",
        name: "Service Unavailable",
        element: <ServiceUnavailable />,
    },
    {
        path: "/maintenance",
        name: "Maintenance",
        element: <MaintenancePages />,
    },
];
export const appRoutes = [
    ...initialRoutes,
    ...generalRoutes,
    ...uiRoutes,
    ...customPagesRoutes,
    ...appsRoutes,
    ...iconsRoutes,
    ...extendedRoutes,
    ...chartsRoutes,
    ...invoicesRoutes,
    ...formsRoutes,
    ...tablesRoutes,
    ...mapsRoutes,
];
export const publicRoutes = [
    ...authRoutes,
    ...layoutsRoutes,
    ...otherPublicRoutes,
];
